import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { encodeId } from '../utils/idCodec';
import {
  Breadcrumb,
  Carousel,
  ProductCard,
  useCart,
  useToast,
  type CarouselSlide,
} from '@kss/ui';
import { useAuth } from '@kss/auth';
import { ArrowUp, ChevronDown, Columns2, Columns3, Columns4, List, SlidersHorizontal, Star, X } from 'lucide-react';
import { type ShopProduct } from './data';
import { loadShopCatalog, type ShopCategory, type ShopCatalogSource } from './productAdapter';
import { TopCategoriesTiles } from './TopCategoriesTiles';
import { publicCatalogService, type Banner, type Brand } from '@kss/api';
import { toAbsoluteImageUrl } from '../utils/imageUrl';
import { useWishlist } from '../account/wishlistStorage';

// 1 / 2 / 3 / 4 column density. The switcher renders before the sort
// dropdown (layout control before content control) and is visible at
// every breakpoint so shoppers can pick density without opening filters.
const COLUMN_OPTIONS = [
  { cols: 1, label: '1', icon: List },
  { cols: 2, label: '2', icon: Columns2 },
  { cols: 3, label: '3', icon: Columns3 },
  { cols: 4, label: '4', icon: Columns4 },
] as const;

/**
 * Build a hero carousel slide from a live `/api/Public/banners` row.
 * The backend stores `imageUrl` as a tenant-relative path
 * (`/uploads/{tenantId}/misc/.../banner.png`). The Vite dev server +
 * IIS prod proxy both forward `/uploads/*` to the API host, so the
 * relative URL just works as an `<img src>`.
 */
function bannerSlide(b: Banner): CarouselSlide {
  /* Auto-detect video URLs by extension. When a panel admin pastes
     `https://cdn.example.com/sale.mp4` as the banner image URL, we
     route it through the Carousel's `videoUrl` slot so it plays as a
     full-bleed video that advances the slide on `ended`. Image URLs
     keep the existing `<img>` + content overlay path. */
  const mediaUrl = b.imageUrl ?? '';
  const isVideo = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i.test(mediaUrl);
  const absoluteMedia = mediaUrl ? toAbsoluteImageUrl(mediaUrl) : '';

  const overlay = (
    <div className="relative h-full w-full">
      {/* Dark overlay so the title stays readable over a busy image
          or video. */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/30 to-transparent" />
      <div className="relative flex h-full items-center justify-between gap-3 px-4 text-white sm:px-8">
        <div className="min-w-0 max-w-md">
          {b.subtitle && (
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider opacity-90 sm:text-xs">
              {b.subtitle}
            </div>
          )}
          {b.title?.trim() && (
            /* Skip the title element entirely when the banner row
               has no headline — keeps image-only banners clean
               instead of rendering an empty bold block. */
            <div className="text-lg font-bold leading-tight sm:text-3xl md:text-4xl">
              {b.title}
            </div>
          )}
          {b.ctaLabel && b.ctaUrl && (
            <a
              href={b.ctaUrl}
              className="mt-2 inline-block rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-content-primary hover:bg-white/90 sm:mt-4 sm:px-4 sm:py-2 sm:text-sm"
            >
              {b.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  );

  if (isVideo) {
    return {
      id: b.bannerId ?? `banner-${b.title}`,
      videoUrl: absoluteMedia,
      overlay,
    };
  }

  return {
    id: b.bannerId ?? `banner-${b.title}`,
    content: (
      <div className="relative h-full w-full">
        {absoluteMedia ? (
          <img
            src={absoluteMedia}
            alt={b.title ?? 'Banner'}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
        {/* Dark overlay so the title stays readable over a busy image. */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/30 to-transparent" />
        <div className="relative flex h-full items-center justify-between gap-3 px-4 text-white sm:px-8">
          <div className="min-w-0 max-w-md">
            {b.subtitle && (
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider opacity-90 sm:text-xs">
                {b.subtitle}
              </div>
            )}
            <div className="text-lg font-bold leading-tight sm:text-3xl md:text-4xl">
              {b.title}
            </div>
            {b.ctaLabel && b.ctaUrl && (
              <a
                href={b.ctaUrl}
                className="mt-2 inline-block rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-content-primary hover:bg-white/90 sm:mt-4 sm:px-4 sm:py-2 sm:text-sm"
              >
                {b.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    ),
  };
}

const SORTS = [
  { id: 'pop', label: 'Popularity' },
  { id: 'new', label: 'New arrivals' },
  { id: 'price-asc', label: 'Price: Low to High' },
  { id: 'price-desc', label: 'Price: High to Low' },
  { id: 'rating', label: 'Customer rating' },
] as const;

type SortId = (typeof SORTS)[number]['id'];
const SORT_IDS = SORTS.map((s) => s.id) as readonly SortId[];
const isSortId = (v: string | null): v is SortId =>
  v != null && (SORT_IDS as readonly string[]).includes(v);

export default function ShopList() {
  const cart = useCart();
  const toast = useToast();
  const { isAuthenticated, user } = useAuth();
  const wishlist = useWishlist(user?.id);
  const navigate = useNavigate();

  /* URL parameter contract — every footer / header link encodes its
     intent in the query string so a refresh / share preserves the
     view. Supported params (bug ref: AI Agent — footer links
     "New Arrivals / Best Sellers / Deals" not working):
       ?q=<text>        — free-text search
       ?c=<categoryId>  — preselect category (set by breadcrumb / mega-menu)
       ?sort=<sortId>   — pop | new | price-asc | price-desc | rating
       ?sale=true       — filter to discounted products only
       ?b=<brand>       — preselect one brand (emitted by the Brands mega-menu)
       ?maxPrice=<n>    — price ceiling (drives the "Under ₹499" deal tiles)

     `b` and `maxPrice` were emitted by the header menus long before
     they were read here, so those links silently landed on an
     unfiltered catalog. They are parsed below for that reason. */
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') ?? '').trim();
  const initialSort: SortId = (() => {
    const fromUrl = searchParams.get('sort');
    return isSortId(fromUrl) ? fromUrl : 'pop';
  })();
  const initialCategory = searchParams.get('c') ?? 'all';
  const salesOnly = searchParams.get('sale') === 'true';
  /** Price ceiling from the URL, ignoring junk and non-positive values. */
  const parseMaxPrice = (raw: string | null): number | null => {
    const n = Number(raw);
    return raw != null && Number.isFinite(n) && n > 0 ? n : null;
  };

  const [category, setCategory] = useState<string>(initialCategory);
  const [maxPrice, setMaxPrice] = useState(parseMaxPrice(searchParams.get('maxPrice')) ?? 20000);
  const [minRating, setMinRating] = useState(0);
  const [brands, setBrands] = useState<Set<string>>(() => {
    const b = (searchParams.get('b') ?? '').trim();
    return b ? new Set([b]) : new Set<string>();
  });
  // Default to 4 columns on desktop to match the storefront density used
  // by store.kristysoftware.com. Mobile / tablet collapse via `gridClass`.
  const [cols, setCols] = useState(4);
  const [sort, setSort] = useState<SortId>(initialSort);

  // Keep state in sync when the URL changes (e.g. user clicks a footer
  // link while already on /shop — react-router won't unmount the page).
  useEffect(() => {
    const next = searchParams.get('sort');
    if (isSortId(next) && next !== sort) setSort(next);
    const c = searchParams.get('c') ?? 'all';
    if (c !== category) setCategory(c);
    /* Price ceiling. Absent `maxPrice` deliberately does NOT reset the
       slider — a shopper who dragged it to ₹5,000 and then clicked a
       category link should keep their ceiling. Only an explicit value
       in the URL overrides it. */
    const mp = parseMaxPrice(searchParams.get('maxPrice'));
    if (mp != null && mp !== maxPrice) setMaxPrice(mp);
    /* Brand. Same rule: only an explicit `?b=` replaces the selection,
       so navigating away from a brand link doesn't wipe manually
       ticked brand checkboxes. */
    const b = (searchParams.get('b') ?? '').trim();
    if (b && !(brands.size === 1 && brands.has(b))) setBrands(new Set([b]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Live catalog — bound to /api/Product/all + /api/Category/all so
  // the storefront and the panel show the same products. The source
  // tag distinguishes 'live' (real catalog), 'unauth' (anonymous user
  // hitting an auth-required backend — show sign-in CTA), 'empty'
  // (tenant has no products yet), and 'mock' (last-resort offline
  // fallback).
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [shopCategories, setShopCategories] = useState<ShopCategory[]>([
    { id: 'all', label: 'All', count: 0 },
  ]);
  // Catalog source tag — kept for future telemetry hooks. Currently
  // unused at render time (the empty state no longer distinguishes
  // 'live' vs 'mock' since mock has been removed).
  const [, setCatalogSource] = useState<ShopCatalogSource>('live');
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Live hero banners from /api/Public/banners. When the tenant has
  // no active banner rows the carousel block is hidden entirely —
  // there is no static / mock fallback any more.
  const [banners, setBanners] = useState<Banner[]>([]);
  useEffect(() => {
    let cancelled = false;
    publicCatalogService
      .banners()
      .then((rows) => {
        if (cancelled) return;
        const active = (rows ?? [])
          .filter((b) => b.isActive !== false)
          .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0));
        setBanners(active);
      })
      .catch(() => {
        /* silent — empty banner list means no carousel renders */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const heroSlides = useMemo<CarouselSlide[]>(
    () => banners.map(bannerSlide),
    [banners],
  );

  // Live brand list for the sidebar filter — sourced from
  // /api/Public/brands so the chips match the tenant's actual catalog.
  // The previous hard-coded `BRANDS` array (derived from the local
  // mock product list) has been removed.
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    publicCatalogService
      .brands()
      .then((rows) => {
        if (cancelled) return;
        setBrandOptions(
          (rows ?? [])
            .filter((b) => b.isActive !== false && b.name)
            .map((b) => b.name!)
            .sort((a, b) => a.localeCompare(b)),
        );
      })
      .catch(() => {
        /* silent — brand filter just shows nothing */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingCatalog(true);
    loadShopCatalog()
      .then((cat) => {
        if (cancelled) return;
        setProducts(cat.products);
        setShopCategories(cat.categories);
        setCatalogSource(cat.source);
      })
      .catch(() => {
        if (cancelled) return;
        // Surface a soft toast — page still renders an empty state.
        toast.error('Catalog failed', 'Could not load products from the server.');
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });
    return () => {
      cancelled = true;
    };
    // toast is stable from the provider; intentionally no dep on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the auth state flips (sign-in / sign-out), refetch so the
  // storefront swaps from the unauth empty state to the live catalog
  // (or vice versa) without a full page reload.
  useEffect(() => {
    let cancelled = false;
    setLoadingCatalog(true);
    loadShopCatalog()
      .then((cat) => {
        if (cancelled) return;
        setProducts(cat.products);
        setShopCategories(cat.categories);
        setCatalogSource(cat.source);
      })
      .catch(() => {
        // already toasted above on first mount
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Show a "scroll to top" floating arrow once the user scrolls past
  // ~600px so it doesn't compete with the hero on first paint.
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const filtered = useMemo(() => {
    let rows: ShopProduct[] = [...products];
    /* Case-insensitive for the same reason as brands below: the mega-menu
       emits `?c=<name.toLowerCase()>` while the sidebar contributes the
       stored casing, and an exact compare made every mega-menu category
       link return an empty grid. */
    if (category !== 'all') {
      const wanted = category.trim().toLowerCase();
      rows = rows.filter((p) => (p.category ?? '').trim().toLowerCase() === wanted);
    }
    rows = rows.filter((p) => p.price <= maxPrice);
    if (minRating > 0) rows = rows.filter((p) => (p.rating ?? 0) >= minRating);
    /* Case-insensitive: the sidebar checkboxes contribute exact stored
       casing ("Nike") while `?b=` arrives lowercased from the mega-menu,
       and an exact-match compare silently returned zero rows for the
       latter. */
    if (brands.size > 0) {
      const wanted = new Set([...brands].map((b) => b.trim().toLowerCase()));
      rows = rows.filter((p) => wanted.has((p.brand ?? '').trim().toLowerCase()));
    }
    /* `?sale=true` from the footer "Deals" link — keep only rows that
       actually have a discount (originalPrice > price). */
    if (salesOnly) {
      rows = rows.filter(
        (p) => p.originalPrice != null && p.originalPrice > p.price,
      );
    }
    if (query) {
      // Tokenised match — every whitespace-separated word in the query
      // must appear somewhere in the haystack (title + brand +
      // description + category + concatenated spec values). Case-
      // insensitive. Order-independent so "headphones wireless"
      // and "wireless headphones" return the same hits.
      const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
      rows = rows.filter((p) => {
        const haystack = [
          p.title,
          p.brand,
          p.description,
          p.category ?? '',
          (p.specs ?? []).map((s) => `${s.label} ${s.value}`).join(' '),
        ]
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => haystack.includes(t));
      });
    }
    switch (sort) {
      case 'price-asc': rows.sort((a, b) => a.price - b.price); break;
      case 'price-desc': rows.sort((a, b) => b.price - a.price); break;
      /* Rating desc, ties broken by review volume — mirrors the server's
         own `rating` sort so a client-side re-sort of a page can't
         disagree with the order the API would have returned. */
      case 'rating':
        rows.sort(
          (a, b) =>
            (b.rating ?? 0) - (a.rating ?? 0) || (b.reviews ?? 0) - (a.reviews ?? 0),
        );
        break;
      case 'new':
        /* Newest first by real creation date.

           This used to be `rows.reverse()`, justified by a comment
           claiming the API returns latest-first. It does not:
           `/Public/products` defaults to the `popularity` sort
           (rating desc, then reviews desc), so reversing it surfaced
           the WORST-rated products under a "New arrivals" heading.
           `createdAt` ships on the card DTO — use it. Undated rows
           sort last rather than jumping to the top. */
        rows.sort((a, b) => {
          const ta = a.createdAt ? Date.parse(a.createdAt) : NaN;
          const tb = b.createdAt ? Date.parse(b.createdAt) : NaN;
          const va = Number.isNaN(ta) ? -Infinity : ta;
          const vb = Number.isNaN(tb) ? -Infinity : tb;
          return vb - va;
        });
        break;
      /* 'pop' — best sellers, by review volume. Now that the adapter
         actually populates `reviews` this orders the grid; before, every
         comparison was 0 − 0 and the sort was a no-op. */
      default: rows.sort((a, b) => (b.reviews ?? 0) - (a.reviews ?? 0));
    }
    return rows;
  }, [products, category, maxPrice, minRating, brands, query, sort, salesOnly]);

  const handleAdd = (p: ShopProduct) => {
    if (!isAuthenticated) {
      toast.info('Sign in to continue', 'Log in to start your order.');
      window.dispatchEvent(
        new CustomEvent('kss:open-login', { detail: { redirect: '/' } })
      );
      return;
    }
    cart.add(p);
    toast.success('Added to cart', p.title);
  };

  const catCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of products) m[p.category ?? ''] = (m[p.category ?? ''] || 0) + 1;
    m.all = products.length;
    return m;
  }, [products]);

  // Card-width parity with store.kristysoftware.com:
  /* Orphan-safe row (Fix 4 from v2.0). CSS Grid with `1fr` tracks
     stretches orphan items on the last row to fill the row width —
     so 5 products in a 4-col layout end with one 4× wider card on
     row 2. We solve this by forcing each track to a FIXED width
     instead of `1fr`: `repeat(N, minmax(0, calc((100% - (N-1) *
     gap) / N)))`. That keeps every card at the same exact width
     and lets orphans sit left-aligned at the same size as their
     row-1 siblings.
     `justifyContent: 'start'` (the default) leaves the unused
     trailing track empty rather than spreading the row. */
  function orphanSafeStyle(n: number, gapPx: number): React.CSSProperties {
    return {
      gridTemplateColumns: `repeat(${n}, minmax(0, calc((100% - ${
        (n - 1) * gapPx
      }px) / ${n})))`,
    };
  }
  /* Returns the active column count for the current viewport.
     Mirrors the `gridClass` breakpoint logic above. */
  function activeColsForViewport(density: number, vw: number): number {
    if (density === 1) return 1;
    if (density === 2) return 2;
    if (density === 3) return vw >= 640 ? 3 : 2;
    // density === 4
    if (vw >= 1280) return 4;
    if (vw >= 640) return 3;
    return 2;
  }
  const [vw, setVw] = useState(typeof window === 'undefined' ? 1280 : window.innerWidth);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const gridStyle = orphanSafeStyle(
    activeColsForViewport(cols, vw),
    vw < 640 ? 12 : 16,
  );

  // Outer padding tightened on phones (px-3 / py-4) so the product
  // grid uses full width on narrow screens; restores the standard
  // 24px gutter from sm onwards.
  /* Breadcrumb (Fix 9) — only shown when the shopper has drilled
     past the catalog root via a category filter. The shop landing
     keeps a clean look (no "Home › Shop" by itself). */
  const activeCategoryLabel =
    category !== 'all'
      ? shopCategories.find((c) => c.id === category)?.label ?? category
      : null;

  return (
    /* Gutters unified to 16px (`px-4`) 2026-05-20 so the categories
       sidebar's left edge and the product grid's right edge line up
       with the header's logo / icon cluster. The hero slider below
       still escapes this gutter via the full-bleed wrapper so it
       reaches the viewport edges. Top padding dropped to 0 (2026-05-21)
       so the slider sits 1px below the sticky header; bottom padding
       kept large so the footer doesn't crash into the product grid. */
    <div className="w-full px-4 pb-6 pt-0 sm:pb-10 lg:pb-12">
      {activeCategoryLabel && (
        <Breadcrumb
          className="mb-4"
          items={[
            { label: 'Shop', to: '/shop' },
            { label: activeCategoryLabel },
          ]}
        />
      )}
      {/* Mobile drawer overlay */}
      {filtersOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setFiltersOpen(false)}
        />
      )}

      {/* Hero slider — FULL-BLEED, edge-to-edge of the viewport.
          The full-bleed trick (`relative left-1/2 -ml-[50vw] w-screen`)
          escapes the parent's `max-w-7xl` so the carousel reaches both
          sides of the page even on wide monitors. `rounded-none border-0`
          flattens the Carousel's default rounded card chrome so the
          banner image extends to the page edge with no rounded corners
          or border. Bug ref: AI Agent 2026-05-20 — "slider should be
          show full page and image border radius remove it should be
          flat". */}
      {heroSlides.length > 0 && (
        <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-6 w-screen">
          <div className="block sm:hidden">
            <Carousel slides={heroSlides} aspectRatio="16/9" className="rounded-none border-0" />
          </div>
          <div className="hidden sm:block lg:hidden">
            <Carousel slides={heroSlides} aspectRatio="21/8" className="rounded-none border-0" />
          </div>
          <div className="hidden lg:block">
            <Carousel slides={heroSlides} aspectRatio="24/7" className="rounded-none border-0" />
          </div>
        </div>
      )}

      {/* Top-categories tile block (Fix 2) — sits between the hero
          and the sidebar+grid. Shoppers can jump straight into one
          of the 6 most-stocked categories with a single tap. */}
      <TopCategoriesTiles categories={shopCategories} className="mb-6" />

      {/* Gap shrunk from `lg:gap-10` → `lg:gap-6` so the space between
          the categories rail (left) and the product grid (right) is
          flush with the page-level 16px gutter — keeps margins
          consistent across the row. */}
      <div className="grid gap-4 lg:grid-cols-[240px_1fr] lg:gap-6">
        {/* Sidebar (drawer on mobile, static on lg+) */}
        <aside
          className={[
            'flex flex-col gap-4 bg-surface-app',
            // Mobile: fixed slide-in drawer
            'fixed left-0 top-0 z-50 h-full w-80 max-w-[85vw] overflow-y-auto border-r border-border p-4 transition-transform duration-300',
            filtersOpen ? 'translate-x-0' : '-translate-x-full',
            // Desktop: static, no transform, no border
            'lg:static lg:z-auto lg:w-auto lg:max-w-none lg:translate-x-0 lg:border-0 lg:p-0',
          ].join(' ')}
        >
          {/* Sticky drawer header so the close button stays reachable
              while the user scrolls through brand / rating filters. */}
          <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-2 flex items-center justify-between border-b border-border bg-surface-app px-4 py-3 lg:hidden">
            <span className="text-sm font-semibold">Filters</span>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              aria-label="Close filters"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-surface-card-hover"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Hero carousel (below sidebar on mobile? place categories first) */}
          {/* Filter rail (Fix 3) — sticky from scroll y=120 down on
              desktop so the filter card follows the shopper while
              they scroll through the product grid. The drawer-mobile
              variant doesn't get sticky (it slides in on demand). */}
          <div className="rounded-lg border border-border bg-surface-card p-4 lg:sticky lg:top-20">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-muted">
              Categories
            </div>
            <ul className="space-y-1">
              {shopCategories.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={[
                      'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors',
                      category === c.id
                        ? 'bg-primary-light font-medium text-primary'
                        : 'text-content-secondary hover:bg-surface-card-hover hover:text-content-primary',
                    ].join(' ')}
                  >
                    <span>{c.label}</span>
                    <span className="text-xs text-content-muted">
                      {c.count ?? catCounts[c.id] ?? 0}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* (Sidebar carousel removed — the full-width hero slider
              above the grid is the only banner surface now. The
              sidebar duplicate was overlapping the categories rail
              and made the filter card feel busy. Bug ref: AI Agent
              2026-05-12 — "one slider overlapping categories it
              should be remove".) */}

          {/* Filters */}
          <div className="rounded-lg border border-border bg-surface-card p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-muted">
              Price
            </div>
            <input
              type="range"
              min={500}
              max={20000}
              step={500}
              value={maxPrice}
              onChange={(e) => setMaxPrice(Number(e.target.value))}
              className="w-full accent-primary"
            />
            <div className="mt-1 flex justify-between text-xs text-content-muted">
              <span>₹500</span>
              <span className="font-medium text-content-primary">Up to ₹{maxPrice.toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-surface-card p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-muted">
              Customer rating
            </div>
            <ul className="space-y-1">
              {[4, 3, 2, 0].map((r) => (
                <li key={r}>
                  <button
                    type="button"
                    onClick={() => setMinRating(r)}
                    className={[
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                      minRating === r
                        ? 'bg-primary-light font-medium text-primary'
                        : 'text-content-secondary hover:bg-surface-card-hover',
                    ].join(' ')}
                  >
                    {r > 0 ? (
                      <>
                        <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                        <span>{r} & up</span>
                      </>
                    ) : (
                      <span>All ratings</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {brandOptions.length > 0 && (
          <div className="rounded-lg border border-border bg-surface-card p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-muted">
              Brand
            </div>
            <ul className="space-y-1">
              {brandOptions.map((b) => {
                const checked = brands.has(b);
                return (
                  <li key={b}>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm text-content-secondary hover:bg-surface-card-hover">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setBrands((prev) => {
                            const n = new Set(prev);
                            if (checked) n.delete(b); else n.add(b);
                            return n;
                          });
                        }}
                        className="h-3.5 w-3.5 accent-primary"
                      />
                      {b}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
          )}
        </aside>

        {/* Main area */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* (Hero slider moved up — now spans full width above the
              sidebar/main grid per v2.0 wireframe layout.) */}

          {/* Toolbar — stacks vertically on phones (left group on row
              one, density + sort on row two) so neither group has to
              fight for horizontal space below ~360px. */}
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-card px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-card px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-card-hover lg:hidden"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
              </button>
              <div className="text-xs text-content-secondary">
                <span className="font-semibold text-content-primary">{filtered.length}</span> product{filtered.length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Column-density switcher first, then sort — matches the
                  conventional "layout controls before content controls"
                  ordering used by most shop UIs. */}
              <div className="flex items-center overflow-hidden rounded-md border border-border">
                {COLUMN_OPTIONS.map((o) => {
                  const Icon = o.icon;
                  const active = cols === o.cols;
                  return (
                    <button
                      key={o.cols}
                      type="button"
                      onClick={() => setCols(o.cols)}
                      title={o.cols === 1 ? '1 column' : `${o.label} columns`}
                      className={[
                        'flex h-8 w-8 items-center justify-center transition-colors',
                        active ? 'bg-primary text-primary-foreground' : 'text-content-muted hover:bg-surface-card-hover',
                      ].join(' ')}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  );
                })}
              </div>
              {/* Sort picker — a native select with `appearance-none`
                  + a custom chevron overlay so it matches the rest of
                  the toolbar (the OS-default select has no border / a
                  bigger chevron and looked off on dark mode). */}
              <label className="relative flex items-center gap-1 text-xs text-content-secondary">
                <span className="hidden sm:inline">Sort</span>
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => {
                      const next = e.target.value as SortId;
                      setSort(next);
                      // Mirror into the URL so the view is shareable
                      // and the back button restores the sort.
                      const params = new URLSearchParams(searchParams);
                      if (next === 'pop') params.delete('sort');
                      else params.set('sort', next);
                      setSearchParams(params, { replace: true });
                    }}
                    className="h-8 w-32 appearance-none rounded-md border border-border bg-surface-card px-2 pr-7 text-xs text-content-primary outline-none focus:border-primary"
                  >
                    {SORTS.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-muted" />
                </div>
              </label>
            </div>
          </div>

          {/* Active-search indicator — shown above the grid whenever
              `?q=` is set so the customer always knows they're on a
              filtered view. Tapping the X clears the URL param and
              jumps back to the full catalog. */}
          {query && !loadingCatalog && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-card px-3 py-2 text-xs">
              <span className="text-content-secondary">
                Showing <strong>{filtered.length}</strong>{' '}
                {filtered.length === 1 ? 'result' : 'results'} for
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-light px-2 py-0.5 font-medium text-primary">
                {query}
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete('q');
                    setSearchParams(next, { replace: true });
                  }}
                  className="-mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-primary/20"
                >
                  ×
                </button>
              </span>
            </div>
          )}

          {/* Product grid — loading skeletons while the live catalog
              fetch is in flight, then real cards (or an empty state). */}
          {loadingCatalog ? (
            <div
              className="grid gap-3 sm:gap-4"
              style={gridStyle}
            >
              {Array.from({ length: cols * 2 }).map((_, i) => (
                <div
                  key={i}
                  className="h-72 animate-pulse rounded-lg border border-border bg-surface-card-hover"
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-border bg-surface-card p-12 text-center text-sm text-content-muted">
              {products.length === 0 ? (
                'No products available — the catalog is empty.'
              ) : query ? (
                <>
                  <p>
                    No products match <strong>“{query}”</strong>
                    {category !== 'all' || brands.size > 0
                      ? ' with the current filters'
                      : ''}
                    .
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      next.delete('q');
                      setSearchParams(next, { replace: true });
                    }}
                    className="mt-3 inline-flex items-center gap-1 rounded-md border border-border bg-surface-card px-3 py-1.5 text-xs font-medium text-content-primary hover:bg-surface-card-hover"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                'No products match the selected filters.'
              )}
            </div>
          ) : (
            <div
              className="grid gap-3 sm:gap-4"
              style={gridStyle}
            >
              {filtered.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  /* QuickView removed 2026-05-12 per request — card click
                     navigates straight to the PDP instead of popping a
                     modal. Matches the "tap-card → product page" pattern
                     every other e-commerce surface (Amazon / Flipkart)
                     uses, and removes the wrapper modal that the new
                     80:20 portrait card no longer needed. */
                  onClick={() => navigate(`/shop/${encodeId(p.id)}`)}
                  onAddToCart={() => handleAdd(p)}
                  onToggleWishlist={() => {
                    const id = String(p.id);
                    const added = wishlist.toggle(id);
                    toast.success(
                      added ? 'Saved to wishlist' : 'Removed from wishlist',
                      p.title,
                    );
                  }}
                  isWishlisted={wishlist.has(String(p.id))}
                  /* `compact` previously kicked in at 4-col density —
                     but compact mode forces a fixed 112px image height
                     which breaks the 80:20 image:content ratio the
                     card was redesigned around. Leave compact OFF here
                     so every density (1/2/3/4 col) gets the proper
                     portrait-oriented card; the related-products rail
                     on the PDP still passes `compact` explicitly when
                     row-height parity matters. */
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Floating "scroll to top" arrow — shows after the user has
          scrolled past the hero. Stays anchored to the viewport
          bottom-right so it's reachable on every page length. */}
      {showScrollTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
          className="fixed bottom-6 right-6 z-40 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface-card text-content-primary shadow-lg transition-transform hover:scale-105 hover:bg-surface-card-hover"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
